/**
 * HTML 안전 처리 유틸 — 정본.
 *
 * 2026-09-02 보안 점검에서 게시판 본문·검색 하이라이트·인쇄 팝업 세 곳이
 * 사용자 입력을 이스케이프 없이 HTML 로 넣고 있었다. 세 경우의 처리 방식이 다르다:
 *
 *  - 서식이 필요 없는 값을 HTML 문자열에 끼워 넣을 때 → `escapeHtml`
 *  - 서식이 있는 본문을 렌더할 때                    → `sanitizeRichHtml`
 *  - 검색 하이라이트처럼 태그 한 종류만 살릴 때        → HTML 로 넣지 말고 React 로 쪼갠다
 */
import DOMPurify from 'dompurify';

/** HTML 문자열에 값을 끼워 넣기 전 최소 이스케이프 (인쇄 팝업 등 `document.write` 경로용) */
export const escapeHtml = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * 서식 있는 본문(게시판 등)을 렌더 직전 정화한다.
 *
 * 백엔드도 저장 시 허용 목록으로 정화하지만(main.py `_sanitize_html`),
 * **이미 저장된 옛 글은 옛 정화기를 통과한 것**이라 렌더 시점에 한 겹 더 건다.
 * 두 곳이 같은 방침(허용 목록)이어야 하므로 태그 집합을 백엔드와 맞춰 둔다.
 */
export const sanitizeRichHtml = (html?: string | null): string =>
  DOMPurify.sanitize(html || '', {
    ALLOWED_TAGS: [
      'p', 'div', 'span', 'br', 'hr',
      'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'sub', 'sup', 'mark', 'small',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'a', 'blockquote', 'pre', 'code', 'img', 'font',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'col', 'colgroup',
    ],
    ALLOWED_ATTR: [
      'style', 'href', 'title', 'target', 'rel', 'src', 'alt', 'width', 'height',
      'color', 'face', 'size', 'colspan', 'rowspan', 'align', 'valign', 'type', 'start',
    ],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|\/|#|\.{0,2}\/|data:image\/(?:png|jpe?g|gif|webp|bmp);base64,)/i,
    ADD_ATTR: ['target'],
  });
